import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const BG = '#0B1120';
const PRIMARY = '#00D2FF';
const SECONDARY = '#7B2FFF';
const ACCENT = '#FF6B9D';

const phaseOpacity = (frame: number, start: number, end: number, fade = 18) => {
  const fadeIn = interpolate(frame, [start, start + fade], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fadeOut = interpolate(frame, [end - fade, end], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
};

const BenzeneRing: React.FC<{
  cx: number;
  cy: number;
  r: number;
  frame: number;
  bondPulse: number;
}> = ({ cx, cy, r, frame, bondPulse }) => {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
  }
  const hexPath = `M ${pts.join(' L ')} Z`;
  const innerR = r * 0.55;
  const circle = (i: number, color: string, label: string) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    const x = cx + innerR * Math.cos(a);
    const y = cy + innerR * Math.sin(a);
    const bob = Math.sin(frame * 0.08 + i) * 3;
    return (
      <g key={i}>
        <circle
          cx={x}
          cy={y + bob}
          r={30}
          fill={color}
          stroke="rgba(255,255,255,0.35)"
          strokeWidth={2.5}
        />
        <text
          x={x}
          y={y + bob + 9}
          textAnchor="middle"
          fill="#fff"
          fontSize={26}
          fontWeight={700}
          fontFamily="system-ui, sans-serif"
        >
          {label}
        </text>
      </g>
    );
  };
  return (
    <svg width={r * 3.2} height={r * 3.2} viewBox={`${cx - r * 1.6} ${cy - r * 1.6} ${r * 3.2} ${r * 3.2}`}>
      <defs>
        <radialGradient id="electronCloud" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.45} />
          <stop offset="70%" stopColor={SECONDARY} stopOpacity={0.12} />
          <stop offset="100%" stopColor={BG} stopOpacity={0} />
        </radialGradient>
        <filter id="glowChem">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <ellipse
        cx={cx}
        cy={cy}
        rx={r * 1.35 + bondPulse * 8}
        ry={r * 1.25 + bondPulse * 6}
        fill="url(#electronCloud)"
        opacity={0.85}
      />
      <path
        d={hexPath}
        fill="none"
        stroke={PRIMARY}
        strokeWidth={4 + bondPulse * 2}
        filter="url(#glowChem)"
        opacity={0.9}
      />
      <path
        d={hexPath}
        fill="none"
        stroke={SECONDARY}
        strokeWidth={2}
        strokeDasharray="8 14"
        opacity={0.5 + bondPulse * 0.2}
        transform={`rotate(${frame * 0.15} ${cx} ${cy})`}
      />
      {circle(0, ACCENT, 'C')}
      {circle(1, '#5eead4', 'C')}
      {circle(2, ACCENT, 'C')}
      {circle(3, '#5eead4', 'C')}
      {circle(4, ACCENT, 'C')}
      {circle(5, '#5eead4', 'C')}
    </svg>
  );
};

const CarbonChain: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const n = 5;
  const baseY = 160;
  const spacing = 140;
  const wiggle = (i: number) => Math.sin(frame * 0.06 + i * 0.8) * 8;
  return (
    <svg width={720} height={300} style={{ opacity }}>
      <defs>
        <linearGradient id="bondGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={PRIMARY} />
          <stop offset="100%" stopColor={SECONDARY} />
        </linearGradient>
      </defs>
      {Array.from({ length: n - 1 }, (_, i) => {
        const x1 = 80 + i * spacing;
        const x2 = 80 + (i + 1) * spacing;
        const y1 = baseY + wiggle(i);
        const y2 = baseY + wiggle(i + 1);
        const dash = interpolate(frame % 90, [0, 45], [0, 24], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        return (
          <g key={i}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="url(#bondGrad)"
              strokeWidth={7}
              strokeLinecap="round"
            />
            <line
              x1={x1}
              y1={y1 - 20}
              x2={x2}
              y2={y2 - 20}
              stroke={ACCENT}
              strokeWidth={4}
              strokeDasharray={`${dash} 40`}
              opacity={0.7}
            />
          </g>
        );
      })}
      {Array.from({ length: n }, (_, i) => {
        const x = 80 + i * spacing;
        const y = baseY + wiggle(i);
        return (
          <g key={`a-${i}`}>
            <circle cx={x} cy={y} r={36} fill="#1e293b" stroke={PRIMARY} strokeWidth={4} />
            <text
              x={x}
              y={y + 11}
              textAnchor="middle"
              fill="#fff"
              fontSize={28}
              fontWeight={700}
              fontFamily="system-ui, sans-serif"
            >
              C
            </text>
            {i < n - 1 && (
              <text
                x={x + spacing / 2}
                y={y - 52}
                textAnchor="middle"
                fill="rgba(255,255,255,0.5)"
                fontSize={26}
                fontFamily="system-ui, sans-serif"
              >
                σ 键
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const ReactionCard: React.FC<{
  title: string;
  formula: string;
  desc: string;
  children: React.ReactNode;
  accent: string;
  delay: number;
  frame: number;
  fps: number;
}> = ({ title, formula, desc, children, accent, delay, frame, fps }) => {
  const localF = Math.max(0, frame - delay);
  const p = spring({
    frame: localF,
    fps,
    config: { damping: 16, stiffness: 100 },
  });
  const y = interpolate(p, [0, 1], [40, 0]);
  const op = interpolate(p, [0, 1], [0, 1]);
  return (
    <div
      style={{
        width: '100%',
        padding: '22px 26px',
        boxSizing: 'border-box' as const,
        borderRadius: 20,
        background: `linear-gradient(145deg, ${accent}22, rgba(15,20,25,0.92))`,
        border: `2px solid ${accent}55`,
        backdropFilter: 'blur(12px)',
        transform: `translateY(${y}px)`,
        opacity: op,
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 800, color: accent, marginBottom: 6 }}>{title}</div>
      <div
        style={{
          fontFamily: 'Georgia, serif',
          fontSize: 26,
          color: 'rgba(255,255,255,0.88)',
          marginBottom: 10,
        }}
      >
        {formula}
      </div>
      <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: 14 }}>
        {desc}
      </div>
      {children}
    </div>
  );
};

const AdditionDiagram: React.FC<{ t: number }> = ({ t }) => {
  const uid = React.useId().replace(/:/g, '');
  const merge = interpolate(t, [0, 1], [80, 0]);
  const mid = `add-arr-${uid}`;
  return (
    <svg width="100%" height={100} viewBox="0 0 360 100">
      <defs>
        <marker id={mid} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <polygon points="0 0, 8 4, 0 8" fill={SECONDARY} />
        </marker>
      </defs>
      <circle cx={70 - merge * 0.3} cy={50} r={18} fill={PRIMARY} opacity={0.9} />
      <circle cx={130 + merge * 0.2} cy={50} r={16} fill={ACCENT} opacity={0.9} />
      <path
        d={`M 160 50 L ${220 + merge * 0.5} 50`}
        stroke={SECONDARY}
        strokeWidth={3}
        markerEnd={`url(#${mid})`}
      />
      <circle cx={280} cy={50} r={28} fill="none" stroke={PRIMARY} strokeWidth={3} />
    </svg>
  );
};

const SubstitutionDiagram: React.FC<{ t: number }> = ({ t }) => {
  const swap = interpolate(t, [0, 1], [0, 1]);
  return (
    <svg width="100%" height={100} viewBox="0 0 360 100">
      <rect
        x={40}
        y={25}
        width={100}
        height={50}
        rx={10}
        fill="rgba(0,210,255,0.15)"
        stroke={PRIMARY}
        strokeWidth={2}
      />
      <text x={90} y={58} textAnchor="middle" fill="#fff" fontSize={22} fontFamily="sans-serif">
        R–X
      </text>
      <text x={180} y={58} textAnchor="middle" fill={ACCENT} fontSize={28} fontWeight={700}>
        +
      </text>
      <circle cx={260} cy={50} r={22} fill={SECONDARY} opacity={0.35 + swap * 0.4} />
      <text x={260} y={57} textAnchor="middle" fill="#fff" fontSize={22}>
        Nu
      </text>
      <text x={320} y={58} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize={24}>
        → R–Nu
      </text>
    </svg>
  );
};

const EliminationDiagram: React.FC<{ t: number }> = ({ t }) => {
  const pull = interpolate(t, [0, 1], [0, 35]);
  return (
    <svg width="100%" height={100} viewBox="0 0 360 100">
      <line x1={60} y1={70} x2={140} y2={30} stroke={PRIMARY} strokeWidth={4} />
      <line x1={140} y1={70} x2={220} y2={30} stroke={ACCENT} strokeWidth={4} />
      <circle cx={160 + pull} cy={75} r={12} fill={SECONDARY} />
      <text x={250} y={55} fill="#fff" fontSize={22} fontFamily="sans-serif">
        脱去小分子
      </text>
    </svg>
  );
};

const OxidationDiagram: React.FC<{ t: number }> = ({ t }) => {
  const h = interpolate(t, [0, 1], [0, 40]);
  return (
    <svg width="100%" height={100} viewBox="0 0 360 100">
      <text x={50} y={45} fill="rgba(255,255,255,0.6)" fontSize={22}>
        失电子
      </text>
      <path
        d={`M 130 55 Q 200 ${55 - h} 270 55`}
        fill="none"
        stroke={ACCENT}
        strokeWidth={3}
      />
      <circle cx={270} cy={55} r={14} fill={PRIMARY} />
      <text x={300} y={60} fill="#FFE66D" fontSize={22} fontWeight={700}>
        O
      </text>
    </svg>
  );
};

export const OrganicChemistryAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({ frame, fps, config: { damping: 14, stiffness: 90 } });
  const titleY = interpolate(titleSpring, [0, 1], [-50, 0]);

  const bondPulse = Math.sin(frame * 0.12) * 0.5 + 0.5;

  const pTitle = phaseOpacity(frame, 0, 105, 20);
  const pStruct = phaseOpacity(frame, 85, 220, 22);
  const pReact = phaseOpacity(frame, 200, 480, 24);
  const pSum = phaseOpacity(frame, 455, 540, 20);

  const reactLocal = Math.max(0, frame - 200);
  const cardBase = 215;
  const whichCard = Math.min(3, Math.floor(reactLocal / 70));

  const diagT = interpolate((frame % 60) / 60, [0, 1], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse 120% 80% at 20% 20%, ${SECONDARY}22 0%, transparent 50%),
          radial-gradient(ellipse 90% 70% at 85% 75%, ${PRIMARY}18 0%, transparent 45%),
          linear-gradient(180deg, #08090c 0%, ${BG} 40%, #0F1419 100%)`,
        fontFamily: 'system-ui, -apple-system, PingFang SC, "Hiragino Sans GB", sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 48,
          left: 72,
          right: 72,
          opacity: pTitle * titleSpring,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 76,
            fontWeight: 900,
            letterSpacing: -1,
            background: `linear-gradient(110deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          有机化学基础
        </h1>
        <p style={{ margin: '14px 0 0', fontSize: 32, color: 'rgba(255,255,255,0.5)' }}>
          分子结构与反应类型
        </p>
        <div
          style={{
            marginTop: 22,
            height: 4,
            width: interpolate(titleSpring, [0, 1], [0, 420]),
            borderRadius: 2,
            background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          top: 200,
          left: 80,
          opacity: pStruct,
          display: 'flex',
          gap: 60,
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 34, fontWeight: 800, color: PRIMARY, marginBottom: 16 }}>
            苯环与离域 π 键
          </div>
          <BenzeneRing cx={160} cy={160} r={130} frame={frame} bondPulse={bondPulse} />
        </div>
        <div>
          <div style={{ fontSize: 34, fontWeight: 800, color: ACCENT, marginBottom: 16 }}>
            碳链与 σ 键骨架
          </div>
          <CarbonChain frame={frame} opacity={1} />
          <p style={{ maxWidth: 1000, fontSize: 26, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
            共价键连接原子；电子云在核间重叠区域概率最高，动画示意离域与振动。
          </p>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 160,
          left: 64,
          right: 64,
          opacity: pReact,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr auto',
          gridTemplateRows: '1fr 1fr',
          gap: 28,
        }}
      >
        <ReactionCard
          title="加成反应"
          formula="C=C + XY → C–C–X–Y"
          desc="不饱和键打开，原子或基团加在两端，典型如烯烃与卤素加成。"
          accent={PRIMARY}
          delay={cardBase}
          frame={frame}
          fps={fps}
        >
          <AdditionDiagram t={whichCard === 0 ? diagT : 1} />
        </ReactionCard>
        <ReactionCard
          title="取代反应"
          formula="R–X + Nu⁻ → R–Nu + X⁻"
          desc="某一基团被另一基团替换，常见于亲核取代机理。"
          accent={SECONDARY}
          delay={cardBase + 8}
          frame={frame}
          fps={fps}
        >
          <SubstitutionDiagram t={whichCard === 1 ? diagT : 1} />
        </ReactionCard>
        <div
          style={{
            gridRow: '1 / 3',
            display: 'flex',
            alignItems: 'center',
            opacity: pSum > 0 ? pSum : 1,
          }}
        >
          <div
            style={{
              padding: '36px 40px',
              borderRadius: 28,
              background: 'rgba(15,23,42,0.75)',
              border: `1px solid ${PRIMARY}44`,
              boxShadow: `0 0 80px ${SECONDARY}33`,
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, fontSize: 36, fontWeight: 800, color: '#fff' }}>
              结构决定性质
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 36, fontWeight: 800, color: '#fff' }}>
              机理连接现象
            </p>
            <p style={{ margin: '16px 0 0', fontSize: 22, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
              识别官能团与键型，
              <br />
              是理解四大反应类型的关键
            </p>
          </div>
        </div>
        <ReactionCard
          title="消除反应"
          formula="邻位脱去 → 生成 π 键"
          desc="从分子中脱去小分子（如 HX、H₂O），形成不饱和键。"
          accent={ACCENT}
          delay={cardBase + 16}
          frame={frame}
          fps={fps}
        >
          <EliminationDiagram t={whichCard === 2 ? diagT : 1} />
        </ReactionCard>
        <ReactionCard
          title="氧化反应"
          formula="氧化数升高 · 失电子"
          desc="有机语境下常指加氧或去氢；与还原成对出现。"
          accent="#FFE66D"
          delay={cardBase + 24}
          frame={frame}
          fps={fps}
        >
          <OxidationDiagram t={whichCard === 3 ? diagT : 1} />
        </ReactionCard>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 18,
          right: 32,
          fontSize: 24,
          color: 'rgba(255,255,255,0.25)',
        }}
      >
        有机化学动画示意 · 教学用途
      </div>
    </AbsoluteFill>
  );
};
